/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import WModal from 'shared_components/components/WModal';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import { useApi } from 'shared_components/context';
import {
  LookupLookupReleaseResponseSourceEnum,
  CreateReleaseAndLinkRequestSourceEnum,
  type LookupReleaseResponsePayload,
} from '../../../../../shared_components/generated/admin';
import type { Row } from '../MasterDiscographyTypes';
import { friendlySaveError } from './saveErrorMessage';
import {
  validateRecordingBeforeRelease,
  validateYearInput,
} from '../../../utils/recordingValidators';
import {
  FIELD_ERROR_CLASS,
  FIELD_LABEL_CLASS,
  FIELD_OPTIONAL_CLASS,
  FIELD_READONLY_CLASS,
  FIELD_SELECT_CLASS,
  MODAL_ACTIONS_CLASS,
  fieldControlClass,
  fieldSelectTextClass,
} from './formStyles';

/**
 * Change/Link Release modal — extracted from RecordingEditForm so it can be
 * reused by the bulk-edit builder.
 *
 * - mode="apply" (recording edit form): performs the create-and-link /
 *   link-earliest API call for a single recording, then hands the updated Row
 *   to `onApplied`.
 * - mode="select" (bulk edit): does the (read-only) lookup so the user can pick
 *   a release, but does NOT link — it just returns the chosen release via
 *   `onApplied`, leaving the actual per-recording linking to the bulk apply.
 *   The second `onApplied` argument carries the linkage itself (see
 *   `ReleaseSelection`); the Row alone only describes how the release should
 *   *display*, which isn't enough to link by.
 *
 * `pinnedRelease` (create only) fixes the source and release id the modal
 * looks up: a Discogs recording is picked off a release's tracklist, and the
 * only release it can be put on is that one. The operator still runs the
 * lookup — it is what tells a release WMC already holds from one it will
 * create — but cannot point it anywhere else.
 */

// Field presentation comes from ./formStyles so this modal looks the same
// whether it was opened from the recording edit form or the bulk-edit builder.
const SELECT_BASE_CLASS = FIELD_SELECT_CLASS;
const selectTextClass = fieldSelectTextClass;

export type ReleaseSourceForm =
  | 'Discogs'
  | 'Deezer'
  | 'Spotify'
  | 'MusicBrainz'
  | 'PPL'
  | 'Other';

/** Validate a 4-digit release year. Returns an error message or null. The
 *  bounds (1920 ≤ year ≤ current year + 2) live in recordingValidators so the
 *  Year of Recording field shares them. */
export function validateReleaseYear(value: string): string | null {
  return validateYearInput(value);
}

function findDuplicate(values: string[]): string | null {
  const seen = new Set<string>();
  for (const v of values) {
    const key = v.toLowerCase();
    if (seen.has(key)) return v;
    seen.add(key);
  }
  return null;
}

function addMultipleValue(
  setter: React.Dispatch<React.SetStateAction<string[]>>,
  value: string
) {
  const trimmed = value.trim();
  if (trimmed) {
    setter((prev) =>
      prev.some((v) => v.toLowerCase() === trimmed.toLowerCase())
        ? prev
        : [...prev, trimmed]
    );
  }
}

function mapReleaseSourceToLookupEnum(
  source: ReleaseSourceForm
):
  | (typeof LookupLookupReleaseResponseSourceEnum)[keyof typeof LookupLookupReleaseResponseSourceEnum]
  | null {
  switch (source) {
    case 'Discogs':
      return LookupLookupReleaseResponseSourceEnum.Discogs;
    case 'Deezer':
      return LookupLookupReleaseResponseSourceEnum.Deezer;
    case 'Spotify':
      return LookupLookupReleaseResponseSourceEnum.Spotify;
    case 'MusicBrainz':
      return LookupLookupReleaseResponseSourceEnum.Musicbrainz;
    case 'PPL':
      return LookupLookupReleaseResponseSourceEnum.Ppl;
    default:
      return null;
  }
}

/** The contract's release-source vocabulary (BE↔DS 4.3.2 / bulk §3.4.4). */
export type ReleaseSourceKey =
  | 'discogs'
  | 'musicbrainz'
  | 'spotify'
  | 'deezer'
  | 'ppl'
  | 'other';

function mapReleaseSourceToContractKey(
  source: ReleaseSourceForm
): ReleaseSourceKey {
  switch (source) {
    case 'Deezer':
      return 'deezer';
    case 'Spotify':
      return 'spotify';
    case 'MusicBrainz':
      return 'musicbrainz';
    case 'PPL':
      return 'ppl';
    case 'Other':
      return 'other';
    default:
      return 'discogs';
  }
}

/**
 * The chosen release as a *linkage*, not a display row.
 *
 * `releaseId` non-null means "this release already exists — link it"; null means
 * "create it from this metadata first, then link". That distinction is the one
 * thing the merged Row can't express, which is why select mode hands this back
 * alongside it.
 */
export interface ReleaseSelection {
  releaseId: string | null;
  source: ReleaseSourceKey;
  /** Release id at the source — or the source URL when `source` is 'other',
   *  which is how 4.3.2 already carries sourceless releases. */
  sourceId: string;
  releaseTitle: string;
  dateReleased: string | null;
  releaseLabels: string[];
  catNumbers: string[];
  mediaType: string;
  format: string;
  sourceReleaseIds?: string[];
}

function mapReleaseSourceToCreateEnum(
  source: ReleaseSourceForm
): CreateReleaseAndLinkRequestSourceEnum {
  switch (source) {
    case 'Discogs':
      return CreateReleaseAndLinkRequestSourceEnum.Discogs;
    case 'Deezer':
      return CreateReleaseAndLinkRequestSourceEnum.Deezer;
    case 'Spotify':
      return CreateReleaseAndLinkRequestSourceEnum.Spotify;
    case 'MusicBrainz':
      return CreateReleaseAndLinkRequestSourceEnum.Musicbrainz;
    case 'PPL':
      return CreateReleaseAndLinkRequestSourceEnum.Ppl;
    case 'Other':
      return CreateReleaseAndLinkRequestSourceEnum.Other;
    default:
      return CreateReleaseAndLinkRequestSourceEnum.Discogs;
  }
}

const MultiValueAddField: React.FC<{
  label: string;
  addLabel: string;
  placeholder: string;
  values: string[];
  onRemove: (index: number) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  value: string;
  setValue: (value: string) => void;
  commit: () => void;
  inputId?: string;
}> = ({
  label,
  addLabel,
  placeholder,
  values,
  onRemove,
  open,
  setOpen,
  value,
  setValue,
  commit,
  inputId,
}) => (
  <div>
    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
      <label htmlFor={inputId} className={FIELD_LABEL_CLASS}>
        {label}
      </label>
      <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
    </div>
    {values.length > 0 && (
      <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2">
        {values.map((v, index) => (
          <div
            key={index}
            className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-formInputBackground tw-text-white"
          >
            <span>{v}</span>
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="tw-ml-1 tw-text-textSeal hover:tw-text-white"
              title="Remove"
            >
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
    )}
    {values.length === 0 && (
      <p className="tw-text-textSeal tw-text-xs tw-mb-2">None</p>
    )}
    <div className="tw-flex tw-flex-col tw-gap-1.5">
      {open ? (
        <>
          <input
            id={inputId}
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && value.trim()) {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                setValue('');
                setOpen(false);
              }
            }}
            className={fieldControlClass({ className: 'tw-flex-1' })}
          />
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setValue('');
                setOpen(false);
              }}
            />
            <WButton
              variant="primary"
              label="Create"
              disabled={!value.trim()}
              onClick={commit}
            />
          </div>
        </>
      ) : (
        <div className="tw-flex tw-justify-end">
          <button
            type="button"
            className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
            onClick={() => {
              setValue('');
              setOpen(true);
            }}
          >
            <i className="wi wi-20px wi-plus tw-mr-1" />
            <span className="tw-underline">{addLabel}</span>
          </button>
        </div>
      )}
    </div>
  </div>
);

export interface ChangeReleaseCurrent {
  release?: string | null;
  year?: string | null;
  format?: string | null;
  mediaType?: string | null;
}

interface ChangeReleaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: 'apply' | 'select';
  userId?: string;
  /** The recording context used for lookup / merge. In bulk 'select' mode this
   *  can be a minimal { id } stand-in (the first selected recording). */
  row: Row | null;
  /** 'select' mode only: every recording the plan will apply to. Given these,
   *  the lookup asks the bulk endpoint about the whole selection instead of
   *  asking the single-recording endpoint about one arbitrary member of it. */
  bulkRecordingIds?: string[];
  current?: ChangeReleaseCurrent;
  currentLabels?: string[];
  currentCatNumbers?: string[];
  availableFormats?: string[];
  availableTypes?: string[];
  /** 'apply' mode only: the recording's current Year of Recording. The
   *  recording-before-release rule is checked against the chosen release's
   *  date before linking, so a release earlier than the recording year is
   *  rejected here rather than by the backend. */
  yearRecorded?: string | null;
  /** `selection` is supplied in 'select' mode only — 'apply' mode has already
   *  done the linking, so there is nothing left for the caller to link. */
  onApplied: (updatedRow: Row, selection?: ReleaseSelection) => void;
  /** Lock the source and release id to these. The fields show the values
   *  read-only and every reset returns to them; the lookup runs against them
   *  alone. See the module comment. */
  pinnedRelease?: { source: ReleaseSourceForm; sourceId: string };
}

export const ChangeReleaseModal: React.FC<ChangeReleaseModalProps> = ({
  isOpen,
  onClose,
  mode = 'apply',
  userId,
  row,
  bulkRecordingIds,
  current = {},
  currentLabels = [],
  currentCatNumbers = [],
  availableFormats = [],
  availableTypes = [],
  yearRecorded,
  onApplied,
  pinnedRelease,
}) => {
  const { adminApi: api } = useApi();

  const [releaseId, setReleaseId] = useState(pinnedRelease?.sourceId ?? '');
  const [releaseSource, setReleaseSource] = useState<ReleaseSourceForm>(
    pinnedRelease?.source ?? 'Discogs'
  );
  // The pin can change while the modal is closed (the operator re-picks from
  // another release, or leaves Discogs altogether), so seed from it on every
  // open rather than only at mount -- and seed the defaults when there is no
  // pin, so a pin that has since gone does not linger as a prefilled id.
  useEffect(() => {
    if (!isOpen) return;
    setReleaseSource(pinnedRelease?.source ?? 'Discogs');
    setReleaseId(pinnedRelease?.sourceId ?? '');
  }, [isOpen, pinnedRelease?.source, pinnedRelease?.sourceId]);
  const [sourceUrl, setSourceUrl] = useState('');
  const [newReleaseTitle, setNewReleaseTitle] = useState('');
  const [newReleaseYear, setNewReleaseYear] = useState('');
  const [newReleaseLabels, setNewReleaseLabels] = useState<string[]>([]);
  const [newReleaseCatNumbers, setNewReleaseCatNumbers] = useState<string[]>(
    []
  );
  const [newLabelInputOpen, setNewLabelInputOpen] = useState(false);
  const [newLabelInputValue, setNewLabelInputValue] = useState('');
  const [newCatNoInputOpen, setNewCatNoInputOpen] = useState(false);
  const [newCatNoInputValue, setNewCatNoInputValue] = useState('');
  const [newReleaseFormat, setNewReleaseFormat] = useState('');
  const [newReleaseType, setNewReleaseType] = useState('');
  const [releaseLookupLoading, setReleaseLookupLoading] = useState(false);
  const [releaseLookupError, setReleaseLookupError] = useState<string | null>(
    null
  );
  const [releaseLookupSuccess, setReleaseLookupSuccess] = useState(false);
  const [releaseLookupNotFound, setReleaseLookupNotFound] = useState(false);
  const [releaseLookupNotLinked, setReleaseLookupNotLinked] = useState(false);
  const [releaseNotLinkedConfirmed, setReleaseNotLinkedConfirmed] =
    useState(false);
  const [lookupReleaseId, setLookupReleaseId] = useState('');
  const [lookupEdgeId, setLookupEdgeId] = useState<string | null>(null);
  const [lookupDateReleased, setLookupDateReleased] = useState<string | null>(
    null
  );
  const [lookupSourceReleaseIds, setLookupSourceReleaseIds] = useState<
    string[]
  >([]);
  const [releaseChangeSubmitError, setReleaseChangeSubmitError] = useState<
    string | null
  >(null);
  const [releaseChangeSubmitLoading, setReleaseChangeSubmitLoading] =
    useState(false);
  const [releaseSameAsCurrentError, setReleaseSameAsCurrentError] =
    useState(false);
  const [newReleaseYearError, setNewReleaseYearError] = useState<string | null>(
    null
  );
  /** Bulk lookup only: how many of the selected recordings are already on this
   *  release. Null until a bulk lookup has run. */
  const [bulkLinkedCount, setBulkLinkedCount] = useState<number | null>(null);

  /** True when the lookup should be a *set* question — "is this release already
   *  linked to any of these recordings?" — rather than a single-recording one.
   *  Only bulk 'select' mode can answer that, and only it needs to. */
  const bulkLookupIds =
    mode === 'select' && bulkRecordingIds?.length ? bulkRecordingIds : null;

  /** True when there is no recording to look the release up *against*.
   *
   *  Create (WMC-515) is the only caller in this state: the recording does not
   *  exist yet, so there is no `parentRecordingId` to send and nothing an
   *  "is it already linked" answer could be about. Contract 4.6 exists for
   *  exactly this — a sibling route per lookup that omits the recording.
   *
   *  Deliberately narrowed to 'select' mode: 'apply' mode performs the link
   *  itself and genuinely cannot proceed without a recording. */
  const recordinglessLookup =
    mode === 'select' && !bulkLookupIds && !row?.id;

  const commitNewLabel = () => {
    const trimmed = newLabelInputValue.trim();
    if (!trimmed) return;
    addMultipleValue(setNewReleaseLabels, trimmed);
    setNewLabelInputValue('');
    setNewLabelInputOpen(false);
  };
  const commitNewCatNo = () => {
    const trimmed = newCatNoInputValue.trim();
    if (!trimmed) return;
    addMultipleValue(setNewReleaseCatNumbers, trimmed);
    setNewCatNoInputValue('');
    setNewCatNoInputOpen(false);
  };

  const handleReleaseLookup = async () => {
    if (
      !userId ||
      (!bulkLookupIds && !row?.id && !recordinglessLookup) ||
      !releaseId.trim()
    ) {
      setReleaseLookupError(
        'User and recording context required. Enter a release ID.'
      );
      return;
    }
    if (releaseSource === 'Other') {
      setReleaseLookupError(
        'Use a catalog source (e.g. Discogs) to look up by ID.'
      );
      return;
    }
    const lookupSource = mapReleaseSourceToLookupEnum(releaseSource);
    if (!lookupSource) {
      setReleaseLookupError('Invalid source for lookup.');
      return;
    }
    const enteredId = releaseId.trim();
    // "Already linked to this release" is a per-recording fact, so it is only a
    // blocking error for a single recording. Across a selection it is normal —
    // and the bulk lookup reports it as a count below rather than a refusal.
    const isSameRelease =
      !bulkLookupIds &&
      (row?.sourceReleaseId ?? []).some(
        (srcId) => srcId === enteredId || srcId.endsWith(`:${enteredId}`)
      );
    if (isSameRelease) {
      setReleaseSameAsCurrentError(true);
      return;
    }

    setReleaseSameAsCurrentError(false);
    setReleaseLookupError(null);
    setReleaseLookupSuccess(false);
    setReleaseLookupNotFound(false);
    setReleaseLookupNotLinked(false);
    setReleaseNotLinkedConfirmed(false);
    setLookupReleaseId('');
    setLookupEdgeId(null);
    setLookupDateReleased(null);
    setLookupSourceReleaseIds([]);
    setBulkLinkedCount(null);
    setReleaseLookupLoading(true);
    try {
      let p: LookupReleaseResponsePayload;
      if (bulkLookupIds) {
        const response = await api.bulkLookupBulkLookupReleaseResponse({
          userId,
          bulkLookupReleaseRequest: {
            source: lookupSource,
            sourceId: enteredId,
            recordingIds: bulkLookupIds,
          },
        });
        p = response.payload;
        // Edges here are per recording, so there is no single `edgeId` to hold
        // — the apply resolves them again for whatever the target set is by
        // then. What this pass is for is telling the user how much of the
        // selection is already on this release before they commit to it.
        const linked = response.recordings.filter((r) => !!r.edgeId).length;
        setBulkLinkedCount(linked);
        if (linked === 0) setReleaseLookupNotLinked(true);
      } else if (recordinglessLookup) {
        // Contract 4.6's sibling route. Same response shape, minus the two
        // fields that need a recording to mean anything.
        const response = await api.lookupReleaseWithoutRecording({
          userId,
          sourceId: enteredId,
          source: lookupSource,
        });
        p = response.payload;
        // No `releaseLookupNotLinked` here, and not an oversight: that warning
        // says "this release is already linked to a different recording",
        // which is the *normal* case when creating one — a release holds many
        // recordings. Showing it would train operators to tick past a warning
        // that does mean something in edit mode.
      } else {
        const response = await api.lookupLookupReleaseResponse({
          userId,
          parentRecordingId: row!.id,
          sourceId: enteredId,
          source: lookupSource,
        });
        p = response.payload;
        setLookupEdgeId(p.edgeId ?? null);
        if (!response.linked) setReleaseLookupNotLinked(true);
      }
      setLookupReleaseId(p.releaseId);
      setLookupDateReleased(p.dateReleased ?? null);
      setLookupSourceReleaseIds(p.sourceReleaseId ?? []);
      if (p.releaseTitle) setNewReleaseTitle(p.releaseTitle);
      if (p.dateReleased) {
        const year =
          p.dateReleased.length >= 4
            ? p.dateReleased.slice(0, 4)
            : p.dateReleased;
        setNewReleaseYear(year);
        setNewReleaseYearError(validateReleaseYear(year));
      }
      if (p.releaseLabels?.length) setNewReleaseLabels([...p.releaseLabels]);
      if (p.catNumbers?.length) setNewReleaseCatNumbers([...p.catNumbers]);
      if (p.format) setNewReleaseFormat(p.format);
      if (p.mediaType) setNewReleaseType(p.mediaType);
      setReleaseLookupSuccess(true);
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      if (status === 404) {
        setReleaseLookupNotFound(true);
      } else {
        const message =
          err instanceof Error ? err.message : 'Release lookup failed';
        setReleaseLookupError(message);
      }
    } finally {
      setReleaseLookupLoading(false);
    }
  };

  const resetReleaseModalState = () => {
    setReleaseId(pinnedRelease?.sourceId ?? '');
    setSourceUrl('');
    setReleaseSource(pinnedRelease?.source ?? 'Discogs');
    setNewReleaseTitle('');
    setNewReleaseYear('');
    setNewReleaseLabels([]);
    setNewReleaseCatNumbers([]);
    setNewLabelInputOpen(false);
    setNewLabelInputValue('');
    setNewCatNoInputOpen(false);
    setNewCatNoInputValue('');
    setNewReleaseFormat('');
    setNewReleaseType('');
    setReleaseLookupError(null);
    setReleaseLookupSuccess(false);
    setReleaseLookupNotFound(false);
    setReleaseLookupNotLinked(false);
    setReleaseNotLinkedConfirmed(false);
    setReleaseSameAsCurrentError(false);
    setLookupReleaseId('');
    setLookupEdgeId(null);
    setLookupDateReleased(null);
    setLookupSourceReleaseIds([]);
    setBulkLinkedCount(null);
    setReleaseChangeSubmitError(null);
    setNewReleaseYearError(null);
  };

  const existingReleaseSourceIds = (): string[] =>
    lookupSourceReleaseIds.length > 0
      ? lookupSourceReleaseIds
      : releaseId.trim()
      ? [releaseId.trim()]
      : [];

  const mergeReleaseIntoRow = (): Row => {
    const base = (row ?? {}) as Row;
    return {
      ...base,
      release: newReleaseTitle.trim(),
      year: newReleaseYear.trim() || '',
      label: newReleaseLabels.join(', '),
      catNumbers: newReleaseCatNumbers.join(', '),
      labelsArray: newReleaseLabels,
      catNumbersArray: newReleaseCatNumbers,
      format: newReleaseFormat,
      mediaType: newReleaseType,
      earliestParentReleaseId: lookupReleaseId || base.earliestParentReleaseId,
      // An 'Other'-source release is identified by URL, and its source id is
      // minted backend-side — unknowable here. Empty is the honest answer;
      // keeping `base` would leave the *previous* release's id on the row and
      // make the "already linked to this release" check below lie.
      sourceReleaseId: releaseId.trim() ? [releaseId.trim()] : [],
      isModified: true,
    };
  };

  /** The chosen release as a linkage (see `ReleaseSelection`). A release the
   *  lookup didn't find — or an 'Other'-source one, which can't be looked up at
   *  all — has no id yet and is created from this metadata on apply. */
  const buildReleaseSelection = (): ReleaseSelection => ({
    releaseId: lookupReleaseId || null,
    source: mapReleaseSourceToContractKey(releaseSource),
    sourceId: releaseSource === 'Other' ? sourceUrl.trim() : releaseId.trim(),
    releaseTitle: newReleaseTitle.trim(),
    dateReleased: lookupDateReleased || newReleaseYear.trim() || null,
    releaseLabels: newReleaseLabels,
    catNumbers: newReleaseCatNumbers,
    mediaType: newReleaseType,
    format: newReleaseFormat,
    sourceReleaseIds: lookupReleaseId ? existingReleaseSourceIds() : [],
  });

  const closeAndReset = () => {
    resetReleaseModalState();
    onClose();
  };

  const handleSubmit = async () => {
    if (newLabelInputOpen || newCatNoInputOpen) {
      setReleaseChangeSubmitError(
        `You have a ${
          newLabelInputOpen ? 'label' : 'catalogue number'
        } being entered. Please click Create to add it, or Cancel, before saving.`
      );
      return;
    }

    const dupNewLabel = findDuplicate(newReleaseLabels);
    if (dupNewLabel) {
      setReleaseChangeSubmitError(`Duplicate label: "${dupNewLabel}"`);
      return;
    }
    const dupNewCatNum = findDuplicate(newReleaseCatNumbers);
    if (dupNewCatNum) {
      setReleaseChangeSubmitError(
        `Duplicate catalogue number: "${dupNewCatNum}"`
      );
      return;
    }

    setReleaseChangeSubmitError(null);

    // Bulk "select" mode: don't perform the per-recording link — just return
    // the chosen release so the bulk apply can handle linking.
    if (mode === 'select') {
      onApplied(mergeReleaseIntoRow(), buildReleaseSelection());
      resetReleaseModalState();
      return;
    }

    if (!userId || !row?.id) return;
    const isCreateAndLinkFlow =
      releaseSource === 'Other' || releaseLookupNotFound;
    setReleaseChangeSubmitLoading(true);
    try {
      if (isCreateAndLinkFlow) {
        const createRequest = {
          source: mapReleaseSourceToCreateEnum(releaseSource),
          releaseTitle: newReleaseTitle.trim(),
          dateReleased: newReleaseYear.trim() || undefined,
          releaseLabels:
            newReleaseLabels.length > 0 ? newReleaseLabels : undefined,
          catNumbers:
            newReleaseCatNumbers.length > 0 ? newReleaseCatNumbers : undefined,
          format: newReleaseFormat || undefined,
          mediaType: newReleaseType || undefined,
          ...(releaseSource === 'Other'
            ? { sourceUrl: sourceUrl.trim() }
            : { sourceId: releaseId.trim() }),
        };
        await api.createAndLinkServiceResponse({
          userId,
          parentRecordingId: row.id,
          createReleaseAndLinkRequest: {
            ...createRequest,
            documentId: row.documentId,
          },
        });
      } else {
        if (!lookupReleaseId) {
          setReleaseChangeSubmitError('Missing release ID from lookup.');
          return;
        }
        await api.linkEarliestServiceResponse({
          userId,
          parentRecordingId: row.id,
          linkReleaseRequest: {
            releaseId: lookupReleaseId,
            sourceReleaseIds: existingReleaseSourceIds().length
              ? existingReleaseSourceIds()
              : undefined,
            edgeId: lookupEdgeId ?? undefined,
            dateReleased: lookupDateReleased ?? undefined,
            releaseTitle: newReleaseTitle.trim(),
            releaseLabels:
              newReleaseLabels.length > 0 ? newReleaseLabels : undefined,
            catNumbers:
              newReleaseCatNumbers.length > 0
                ? newReleaseCatNumbers
                : undefined,
            mediaType: newReleaseType || undefined,
            format: newReleaseFormat || undefined,
            documentId: row.documentId,
          },
        });
      }
      onApplied(mergeReleaseIntoRow());
      resetReleaseModalState();
      onClose();
    } catch (err) {
      setReleaseChangeSubmitError(await friendlySaveError(err, 'release-link'));
    } finally {
      setReleaseChangeSubmitLoading(false);
    }
  };

  const isCreateFlow = releaseSource === 'Other' || releaseLookupNotFound;

  // Recording-before-release rule ('apply' mode only): the date that will be
  // sent on link/create is the looked-up date for a found release and the
  // typed year for a created one — check the same value the backend will.
  const releaseDateToLink = isCreateFlow
    ? newReleaseYear
    : lookupDateReleased ?? newReleaseYear;
  const recordingBeforeReleaseError =
    mode === 'apply'
      ? validateRecordingBeforeRelease(
          yearRecorded,
          releaseDateToLink,
          'release'
        )
      : undefined;

  return (
    <WModal
      isOpen={isOpen}
      onClose={closeAndReset}
      title={isCreateFlow ? 'Create and Link Release' : 'Change Linked Release'}
      modalClasses="tw-max-w-lg tw-w-full tw-mx-4 tw-max-h-[90vh]"
    >
      <div className="tw-mt-4 tw-overflow-y-auto tw-max-h-[calc(90vh-120px)]">
        <div className="tw-mb-4">
          <label
            htmlFor="release-source-input"
            className={`${FIELD_LABEL_CLASS} tw-mb-2`}
          >
            Release Source
          </label>
          <select
            id="release-source-input"
            value={releaseSource}
            disabled={!!pinnedRelease}
            onChange={(e) => {
              const newSource = e.target.value as ReleaseSourceForm;
              setReleaseSource(newSource);
              setReleaseId('');
              setSourceUrl('');
              setReleaseLookupError(null);
              setReleaseLookupSuccess(false);
              setReleaseLookupNotFound(false);
              setReleaseLookupNotLinked(false);
              setReleaseNotLinkedConfirmed(false);
              setReleaseSameAsCurrentError(false);
              if (newSource === 'Other' && !newReleaseTitle.trim()) {
                setNewReleaseTitle(current.release || '');
                setNewReleaseYear(current.year || '');
                setNewReleaseLabels([...currentLabels]);
                setNewReleaseCatNumbers([...currentCatNumbers]);
                setNewReleaseFormat(current.format || '');
                setNewReleaseType(current.mediaType || '');
              }
            }}
            className={`${SELECT_BASE_CLASS} ${selectTextClass(
              !!releaseSource
            )}`}
          >
            <option value="Discogs">Discogs</option>
            <option value="Deezer">Deezer</option>
            <option value="Spotify">Spotify</option>
            <option value="MusicBrainz">MusicBrainz</option>
            <option value="PPL">PPL</option>
            <option value="Other">Other</option>
          </select>
        </div>
        {['Discogs', 'Deezer', 'Spotify', 'MusicBrainz', 'PPL'].includes(
          releaseSource
        ) ? (
          <div className="tw-mb-4">
            <label
              htmlFor="release-id-input"
              className={`${FIELD_LABEL_CLASS} tw-mb-2`}
            >
              Release ID
            </label>
            <div className="tw-flex tw-gap-2 tw-items-center">
              <input
                id="release-id-input"
                type="text"
                value={releaseId}
                readOnly={!!pinnedRelease}
                onChange={(e) => {
                  if (pinnedRelease) return;
                  setReleaseId(e.target.value);
                  setReleaseLookupError(null);
                  setReleaseLookupSuccess(false);
                  setReleaseLookupNotFound(false);
                  setReleaseLookupNotLinked(false);
                  setReleaseNotLinkedConfirmed(false);
                  setReleaseSameAsCurrentError(false);
                }}
                className={fieldControlClass({
                  className: 'tw-flex-1',
                  locked: !!pinnedRelease,
                })}
                placeholder="Enter release ID"
              />
              <WButton
                variant="secondary"
                label={releaseLookupLoading ? 'Looking up…' : 'Lookup'}
                disabled={
                  releaseLookupLoading ||
                  !releaseId.trim() ||
                  !userId ||
                  // A create has no recording, and contract 4.6's sibling route
                  // does not need one. Without this the button is permanently
                  // dead in create mode and the empty state leads nowhere.
                  (!row?.id && !recordinglessLookup)
                }
                onClick={handleReleaseLookup}
              />
            </div>
            {pinnedRelease && (
              <p className="tw-text-gray-400 tw-text-xs tw-mt-2">
                Locked to the {pinnedRelease.source} release the recording was
                picked from.
              </p>
            )}
            {releaseLookupError && (
              <p className="tw-text-westburyRed tw-text-xs tw-mt-2">
                {releaseLookupError}
              </p>
            )}
            {releaseLookupNotFound && (
              <div className="tw-mt-4 tw-space-y-4 tw-pt-4 tw-border-t tw-border-formInputBackground">
                <p className="tw-text-orange-400 tw-text-sm">
                  Release not found in WMC database, please provide details.
                </p>
                <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                  <div className="tw-flex-1 tw-min-w-0">
                    <label
                      htmlFor="change-release-notfound-title"
                      className={`${FIELD_LABEL_CLASS} tw-mb-2`}
                    >
                      Release Title
                    </label>
                    <input
                      id="change-release-notfound-title"
                      type="text"
                      value={newReleaseTitle}
                      onChange={(e) => setNewReleaseTitle(e.target.value)}
                      className={fieldControlClass()}
                      placeholder="Enter release title"
                    />
                  </div>
                  <div className="tw-flex-1 tw-min-w-0">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <label
                        htmlFor="change-release-notfound-year"
                        className={FIELD_LABEL_CLASS}
                      >
                        Year of Release
                      </label>
                      <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
                    </div>
                    <input
                      id="change-release-notfound-year"
                      type="text"
                      value={newReleaseYear}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                        setNewReleaseYear(v);
                        setNewReleaseYearError(validateReleaseYear(v));
                      }}
                      className={fieldControlClass({
                        error: !!newReleaseYearError,
                      })}
                      placeholder="YYYY"
                      maxLength={4}
                    />
                    {newReleaseYearError && (
                      <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>
                        {newReleaseYearError}
                      </p>
                    )}
                    {!newReleaseYearError && recordingBeforeReleaseError && (
                      <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>
                        {recordingBeforeReleaseError}
                      </p>
                    )}
                  </div>
                </div>
                <MultiValueAddField
                  label="Label (Multiple)"
                  addLabel="Add label"
                  placeholder="Enter label"
                  inputId="change-release-notfound-labels-input"
                  values={newReleaseLabels}
                  onRemove={(index) =>
                    setNewReleaseLabels((prev) =>
                      prev.filter((_, i) => i !== index)
                    )
                  }
                  open={newLabelInputOpen}
                  setOpen={setNewLabelInputOpen}
                  value={newLabelInputValue}
                  setValue={setNewLabelInputValue}
                  commit={commitNewLabel}
                />
                <MultiValueAddField
                  label="Catalogue Number (Multiple)"
                  addLabel="Add catalogue number"
                  placeholder="Enter catalogue number"
                  inputId="change-release-notfound-catno-input"
                  values={newReleaseCatNumbers}
                  onRemove={(index) =>
                    setNewReleaseCatNumbers((prev) =>
                      prev.filter((_, i) => i !== index)
                    )
                  }
                  open={newCatNoInputOpen}
                  setOpen={setNewCatNoInputOpen}
                  value={newCatNoInputValue}
                  setValue={setNewCatNoInputValue}
                  commit={commitNewCatNo}
                />
                <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                  <div className="tw-flex-1 tw-min-w-0">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <label
                        htmlFor="change-release-notfound-format"
                        className={FIELD_LABEL_CLASS}
                      >
                        Format
                      </label>
                      <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
                    </div>
                    <select
                      id="change-release-notfound-format"
                      value={newReleaseFormat}
                      onChange={(e) => setNewReleaseFormat(e.target.value)}
                      className={`${SELECT_BASE_CLASS} ${selectTextClass(
                        !!newReleaseFormat
                      )}`}
                    >
                      <option value="">Select Format</option>
                      {availableFormats.map((format) => (
                        <option key={format} value={format}>
                          {format}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="tw-flex-1 tw-min-w-0">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <label
                        htmlFor="change-release-notfound-type"
                        className={FIELD_LABEL_CLASS}
                      >
                        Type
                      </label>
                      <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
                    </div>
                    <select
                      id="change-release-notfound-type"
                      value={newReleaseType}
                      onChange={(e) => setNewReleaseType(e.target.value)}
                      className={`${SELECT_BASE_CLASS} ${selectTextClass(
                        !!newReleaseType
                      )}`}
                    >
                      <option value="">Select Type</option>
                      {availableTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
            {releaseLookupSuccess && (
              <div className="tw-mt-4 tw-space-y-4 tw-pt-4 tw-border-t tw-border-formInputBackground">
                <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                  <div className="tw-flex-1">
                    <label className="tw-block tw-text-textSeal tw-text-xs tw-font-medium tw-mb-1">
                      Release Title
                    </label>
                    <input
                      type="text"
                      value={newReleaseTitle}
                      readOnly
                      className={FIELD_READONLY_CLASS}
                    />
                  </div>
                  <div className="tw-w-full">
                    <label className="tw-block tw-text-textSeal tw-text-xs tw-font-medium tw-mb-1">
                      Year of Release
                    </label>
                    <input
                      type="text"
                      value={newReleaseYear}
                      readOnly
                      className={FIELD_READONLY_CLASS}
                    />
                    {recordingBeforeReleaseError && (
                      <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>
                        {recordingBeforeReleaseError}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="tw-block tw-text-textSeal tw-text-xs tw-font-medium tw-mb-1">
                    Label (Multiple)
                  </label>
                  <div
                    className={`${FIELD_READONLY_CLASS} tw-flex tw-flex-wrap tw-gap-1.5 tw-items-center`}
                  >
                    {newReleaseLabels.length > 0 ? (
                      newReleaseLabels.map((label, index) => (
                        <span
                          key={index}
                          className="tw-inline-flex tw-rounded-xs tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-bg-formInputBackground"
                        >
                          {label}
                        </span>
                      ))
                    ) : (
                      <span className="tw-text-textSeal">—</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="tw-block tw-text-textSeal tw-text-xs tw-font-medium tw-mb-1">
                    Catalogue Number (Multiple)
                  </label>
                  {newReleaseCatNumbers.length > 0 && (
                    <div
                      className={`${FIELD_READONLY_CLASS} tw-flex tw-flex-wrap tw-gap-1.5 tw-items-center`}
                    >
                      {newReleaseCatNumbers.length > 0 ? (
                        newReleaseCatNumbers.map((catNo, index) => (
                          <span
                            key={index}
                            className="tw-inline-flex tw-rounded-xs tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-bg-formInputBackground"
                          >
                            {catNo}
                          </span>
                        ))
                      ) : (
                        <span className="tw-text-textSeal">—</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                  <div className="tw-w-full">
                    <label className="tw-block tw-text-textSeal tw-text-xs tw-font-medium tw-mb-1">
                      Format
                    </label>
                    <input
                      type="text"
                      value={newReleaseFormat}
                      readOnly
                      className={FIELD_READONLY_CLASS}
                    />
                  </div>
                  <div className="tw-w-full">
                    <label className="tw-block tw-text-textSeal tw-text-xs tw-font-medium tw-mb-1">
                      Type
                    </label>
                    <input
                      type="text"
                      value={newReleaseType}
                      readOnly
                      className={FIELD_READONLY_CLASS}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="tw-space-y-4">
            <div>
              <label
                htmlFor="new-release-source-url"
                className={`${FIELD_LABEL_CLASS} tw-mb-2`}
              >
                Source
              </label>
              <input
                id="new-release-source-url"
                type="text"
                value={sourceUrl}
                onChange={(e) => {
                  setSourceUrl(e.target.value);
                }}
                className={fieldControlClass()}
                placeholder="Provide release source details"
              />
            </div>
            <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
              <div className="tw-flex-1 tw-min-w-0">
                <label
                  htmlFor="new-release-title"
                  className={`${FIELD_LABEL_CLASS} tw-mb-2`}
                >
                  Release Title
                </label>
                <input
                  id="new-release-title"
                  type="text"
                  value={newReleaseTitle}
                  onChange={(e) => setNewReleaseTitle(e.target.value)}
                  className={fieldControlClass()}
                  placeholder="Enter release title"
                />
              </div>
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                  <label
                    htmlFor="new-release-year"
                    className={FIELD_LABEL_CLASS}
                  >
                    Year of Release
                  </label>
                  <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
                </div>
                <input
                  id="new-release-year"
                  type="text"
                  value={newReleaseYear}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setNewReleaseYear(v);
                    setNewReleaseYearError(validateReleaseYear(v));
                  }}
                  className={fieldControlClass({
                    error: !!newReleaseYearError,
                  })}
                  placeholder="YYYY"
                  maxLength={4}
                />
                {newReleaseYearError && (
                  <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>
                    {newReleaseYearError}
                  </p>
                )}
                {!newReleaseYearError && recordingBeforeReleaseError && (
                  <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>
                    {recordingBeforeReleaseError}
                  </p>
                )}
              </div>
            </div>
            <MultiValueAddField
              label="Label (Multiple)"
              addLabel="Add label"
              placeholder="Enter label"
              inputId="new-release-labels-input"
              values={newReleaseLabels}
              onRemove={(index) =>
                setNewReleaseLabels((prev) =>
                  prev.filter((_, i) => i !== index)
                )
              }
              open={newLabelInputOpen}
              setOpen={setNewLabelInputOpen}
              value={newLabelInputValue}
              setValue={setNewLabelInputValue}
              commit={commitNewLabel}
            />
            <MultiValueAddField
              label="Catalogue Number (Multiple)"
              addLabel="Add catalogue number"
              placeholder="Enter catalogue number"
              inputId="new-release-catno-input"
              values={newReleaseCatNumbers}
              onRemove={(index) =>
                setNewReleaseCatNumbers((prev) =>
                  prev.filter((_, i) => i !== index)
                )
              }
              open={newCatNoInputOpen}
              setOpen={setNewCatNoInputOpen}
              value={newCatNoInputValue}
              setValue={setNewCatNoInputValue}
              commit={commitNewCatNo}
            />
            <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                  <label
                    htmlFor="new-release-format"
                    className={FIELD_LABEL_CLASS}
                  >
                    Format
                  </label>
                  <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
                </div>
                <select
                  id="new-release-format"
                  value={newReleaseFormat}
                  onChange={(e) => setNewReleaseFormat(e.target.value)}
                  className={`${SELECT_BASE_CLASS} ${selectTextClass(
                    !!newReleaseFormat
                  )}`}
                >
                  <option value="">Select Format</option>
                  {availableFormats.map((format) => (
                    <option key={format} value={format}>
                      {format}
                    </option>
                  ))}
                </select>
              </div>
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                  <label
                    htmlFor="new-release-type"
                    className={FIELD_LABEL_CLASS}
                  >
                    Type
                  </label>
                  <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
                </div>
                <select
                  id="new-release-type"
                  value={newReleaseType}
                  onChange={(e) => setNewReleaseType(e.target.value)}
                  className={`${SELECT_BASE_CLASS} ${selectTextClass(
                    !!newReleaseType
                  )}`}
                >
                  <option value="">Select Type</option>
                  {availableTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
        {releaseLookupNotLinked && (
          <div
            className="tw-rounded-lg tw-border tw-border-orange-500/60 tw-bg-orange-500/10 tw-p-4"
            role="alert"
          >
            <p className="tw-text-orange-400 tw-text-sm tw-font-medium tw-mb-2">
              Confirm Release Linking
            </p>
            <p className="tw-text-gray-300 tw-text-sm tw-mb-4">
              {bulkLookupIds
                ? 'None of the selected recordings are currently linked to this release.'
                : 'This release is already linked to a different recording.'}
            </p>
            <p className="tw-text-gray-300 tw-text-sm tw-mb-4">
              Please double-check that you are linking it to the correct
              {bulkLookupIds ? ' recordings ' : ' recording '}
              and not a different version.
            </p>
            <label
              htmlFor="change-release-not-linked-confirmed"
              className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer"
            >
              <input
                id="change-release-not-linked-confirmed"
                type="checkbox"
                checked={releaseNotLinkedConfirmed}
                onChange={(e) => setReleaseNotLinkedConfirmed(e.target.checked)}
                className="tw-mt-0.5 tw-rounded tw-border-formInputBackground tw-bg-formInputBackground tw-text-orange-500 focus:tw-ring-orange-500"
              />
              <span className="tw-text-sm tw-text-white">
                I have read and understood this warning
              </span>
            </label>
          </div>
        )}
        {/* Some of the selection already on this release is not a problem —
            those recordings keep their existing edge rather than gaining a
            second one — but it is worth saying, because it explains why the
            edit will be a no-op for part of the selection. */}
        {bulkLookupIds && !!bulkLinkedCount && (
          <p className="tw-text-textDove tw-text-xs tw-mt-2 tw-mb-2">
            {bulkLinkedCount} of {bulkLookupIds.length} selected recording
            {bulkLookupIds.length === 1 ? ' is' : 's are'} already linked to
            this release.
          </p>
        )}
        {releaseSameAsCurrentError && (
          <p className="tw-text-westburyRed tw-text-xs tw-mt-2 tw-mb-2">
            This recording is already linked to this release
          </p>
        )}
        {releaseChangeSubmitError && (
          <p className="tw-text-westburyRed tw-text-xs tw-mt-2 tw-mb-2">
            {releaseChangeSubmitError}
          </p>
        )}
        <div
          className={`${MODAL_ACTIONS_CLASS} tw-mt-4 tw-pt-4 tw-border-t tw-border-formInputBackground`}
        >
          <WButton variant="secondary" label="Cancel" onClick={closeAndReset} />
          <WButton
            variant="primary"
            label={
              releaseChangeSubmitLoading
                ? 'Saving…'
                : mode === 'select'
                ? 'Select release'
                : isCreateFlow
                ? 'Create and Link Release'
                : 'Change Linked Release'
            }
            disabled={
              releaseChangeSubmitLoading ||
              releaseSameAsCurrentError ||
              !!newReleaseYearError ||
              !!recordingBeforeReleaseError ||
              (isCreateFlow
                ? !newReleaseTitle.trim() ||
                  (releaseSource === 'Other' && !sourceUrl.trim())
                : !releaseLookupSuccess ||
                  !lookupReleaseId ||
                  (releaseLookupNotLinked && !releaseNotLinkedConfirmed))
            }
            onClick={handleSubmit}
          />
        </div>
      </div>
    </WModal>
  );
};

export default ChangeReleaseModal;
